import React from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  TooltipProps
} from 'recharts';
import { NameType, ValueType } from 'recharts/types/component/DefaultTooltipContent';

interface ModernLineChartProps {
  title?: string;
  data: any[];
  lines: {
    dataKey: string;
    name: string;
    color: string;
    strokeWidth?: number;
    strokeDasharray?: string;
  }[];
  xAxisDataKey: string;
  formatYLabel?: (value: number) => string;
  formatTooltip?: (value: number, name: string) => string;
}

const ModernLineChart: React.FC<ModernLineChartProps> = ({
  title,
  data,
  lines,
  xAxisDataKey,
  formatYLabel,
  formatTooltip
}) => {
  const CustomTooltip = ({ active, payload, label }: TooltipProps<ValueType, NameType>) => {
    if (active && payload && payload.length) {
      // Filter out series with strokeWidth of 0 (hidden series)
      const visiblePayload = payload.filter(entry => {
        const matchingLine = lines.find(line => line.dataKey === entry.dataKey);
        return matchingLine && (matchingLine.strokeWidth === undefined || matchingLine.strokeWidth > 0);
      });

      if (visiblePayload.length === 0) return null;

      return (
        <div 
          className="bg-white p-4 rounded-lg shadow-xl border border-gray-200"
          style={{ 
            zIndex: 9999, 
            pointerEvents: 'auto',
            minWidth: '200px'
          }}
        >
          <p className="text-gray-700 font-medium mb-2">{`Año ${label}`}</p>
          <div className="space-y-2">
            {visiblePayload.map((entry, index) => (
              <div 
                key={`item-${index}`} 
                className="flex items-center gap-2"
              >
                <div 
                  className="w-4 h-4 rounded-full flex-shrink-0" 
                  style={{ backgroundColor: entry.color }}
                ></div>
                <p className="text-sm">
                  <span className="font-medium">{entry.name}: </span>
                  <span className="font-bold">
                    {formatTooltip 
                      ? formatTooltip(entry.value as number, entry.name as string) 
                      : entry.value}
                  </span>
                </p>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full">
      {title && (
        <h3 className="text-center text-lg font-medium text-gray-800 mb-4">{title}</h3>
      )}
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{
            top: 20,
            right: 50,
            left: 50,
            bottom: 10,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey={xAxisDataKey} 
            tick={{ fill: '#4b5563' }}
            axisLine={{ stroke: '#9ca3af' }}
            tickMargin={10}
            padding={{ left: 10, right: 10 }}
          />
          <YAxis 
            tickFormatter={formatYLabel}
            tick={{ fill: '#4b5563' }}
            axisLine={{ stroke: '#9ca3af' }}
            tickMargin={10}
            width={80}
          />
          <Tooltip 
            content={<CustomTooltip />} 
            wrapperStyle={{ zIndex: 9999, pointerEvents: 'auto' }}
            cursor={{ stroke: 'rgba(0, 0, 0, 0.1)', strokeWidth: 1 }}
            isAnimationActive={false}
          />
          <Legend 
            wrapperStyle={{ paddingTop: '10px' }}
            iconType="circle"
            iconSize={10}
          />
          {lines.map((line, index) => (
            <Line
              key={index}
              type="monotone"
              dataKey={line.dataKey}
              name={line.name}
              stroke={line.color}
              strokeWidth={line.strokeWidth || 2}
              strokeDasharray={line.strokeDasharray}
              dot={{ r: 4, strokeWidth: 2, fill: 'white' }}
              activeDot={{ 
                r: 8, 
                strokeWidth: 0, 
                fill: line.color,
                className: "filter drop-shadow-md transition-all duration-300"
              }}
              animationDuration={800}
              animationEasing="ease-out"
              animationBegin={index * 200}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ModernLineChart;